import React, { useEffect, useMemo, useRef } from "react";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import type {
  FormContext,
  VisaType,
} from "@features/port-security/components/SecurityClearence.types.ts";
import {
  uaeStates,
  VISA_ON_ARRIVAL_CODE,
  type PortSecurityFormData,
} from "@features/port-security/schema/portSecurityFormSchema";
import { useFormattedReference } from "@hooks/useFormattedReference.ts";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi.ts";
import { t } from "i18next";
import { FormProvider, useWatch } from "react-hook-form";
import { useOutletContext } from "react-router-dom";

const UAE_NATIONALITY = "United Arab Emirates";
const VISA_GROUP_FIELDS: Array<keyof PortSecurityFormData> = [
  "visaPlaceOfIssue",
  "visaType",
  "visaExpiryDate",
];
const VISA_UPLOAD_FIELD: Array<keyof PortSecurityFormData> = ["visa"];

const hasVisaValue = (value: unknown) => {
  if (value instanceof Date) {
    return !Number.isNaN(value.getTime());
  }

  return String(value ?? "").trim().length > 0;
};

const ResidenceData: React.FC = () => {
  const { formData, updateFormData } = useOutletContext<FormContext>();
  const { control, trigger } = formData;
  const previousVisaSupportFieldsRef = useRef<{
    presentNationality?: string;
    visaPlaceOfIssue?: string;
    residenceVisaNo?: string;
    visaType?: string;
    visaExpiryDate?: Date | string | null;
    visaDateOfIssue?: Date | string | null;
  } | null>(null);
  const previousVisaDetailsPresenceRef = useRef(false);
  const previousVisaOnArrivalRef = useRef<boolean | null>(null);
  const { options: visaTypes } = useFormattedReference<VisaType>(
    REFERENCE_DATA_TYPES.VISA_TYPES,
    "bff-gatepass",
  );
  const { options: nationalities } = useFormattedReference<{
    nationality?: string;
    visaRequired?: boolean;
  }>(REFERENCE_DATA_TYPES.NATIONALITY, "bff-gatepass");
  const visaExemptNationalities = useMemo(
    () =>
      new Set(
        nationalities
          .filter((nationality) => nationality.visaRequired === false)
          .map((nationality) => nationality.nationality?.trim())
          .filter((nationality): nationality is string => Boolean(nationality)),
      ),
    [nationalities],
  );
  const formattedVisaTypes = visaTypes.map((visaType) => ({
    value: visaType.typeCode,
    label: visaType.typeName,
  }));
  const stateOptions = uaeStates.map((state) => ({
    label: state.state,
    value: state.state,
  }));
  const triggerRef = useRef(trigger);

  useEffect(() => {
    triggerRef.current = trigger;
  }, [trigger]);

  const presentNationality = useWatch({
    control,
    name: "presentNationality",
  });
  const hasServed = useWatch({
    control,
    name: "hasServed",
  });
  const visaPlaceOfIssue = useWatch({
    control,
    name: "visaPlaceOfIssue",
  });
  const residenceVisaNo = useWatch({
    control,
    name: "residenceVisaNo",
  });
  const visaType = useWatch({
    control,
    name: "visaType",
  });
  const visaExpiryDate = useWatch({
    control,
    name: "visaExpiryDate",
  });
  const visaDateOfIssue = useWatch({
    control,
    name: "visaDateOfIssue",
  });

  useEffect(() => {
    const previous = previousVisaSupportFieldsRef.current;
    previousVisaSupportFieldsRef.current = {
      presentNationality,
      visaPlaceOfIssue,
      residenceVisaNo,
      visaType,
      visaExpiryDate,
      visaDateOfIssue,
    };

    if (!previous) {
      return;
    }

    const isUaeNationality = presentNationality?.trim() === UAE_NATIONALITY;
    const didClearVisaField =
      (hasVisaValue(previous.visaPlaceOfIssue) && !hasVisaValue(visaPlaceOfIssue)) ||
      (hasVisaValue(previous.residenceVisaNo) && !hasVisaValue(residenceVisaNo)) ||
      (hasVisaValue(previous.visaType) && !hasVisaValue(visaType)) ||
      (hasVisaValue(previous.visaExpiryDate) && !hasVisaValue(visaExpiryDate)) ||
      (hasVisaValue(previous.visaDateOfIssue) && !hasVisaValue(visaDateOfIssue));

    if (!isUaeNationality || !didClearVisaField) {
      return;
    }

    triggerRef.current?.(VISA_GROUP_FIELDS);
  }, [
    presentNationality,
    visaDateOfIssue,
    visaExpiryDate,
    visaPlaceOfIssue,
    visaType,
    residenceVisaNo,
  ]);

  useEffect(() => {
    const normalizedNationality = presentNationality?.trim();
    const isUaeNationality = normalizedNationality === UAE_NATIONALITY;
    const hasVisaDetails = Boolean(
      visaPlaceOfIssue || residenceVisaNo || visaType || visaExpiryDate || visaDateOfIssue,
    );
    const didVisaDetailsPresenceChange = previousVisaDetailsPresenceRef.current !== hasVisaDetails;

    previousVisaDetailsPresenceRef.current = hasVisaDetails;

    if (!isUaeNationality || !didVisaDetailsPresenceChange) {
      return;
    }

    triggerRef.current?.(VISA_GROUP_FIELDS.concat(VISA_UPLOAD_FIELD));
  }, [
    presentNationality,
    visaDateOfIssue,
    visaExpiryDate,
    visaPlaceOfIssue,
    visaType,
    residenceVisaNo,
  ]);

  useEffect(() => {
    const normalizedNationality = presentNationality?.trim();
    const isNonUaeNationality =
      Boolean(normalizedNationality) && normalizedNationality !== UAE_NATIONALITY;

    if (!hasServed || !isNonUaeNationality) {
      return;
    }

    // Keep non-UAE VISA mandatory errors active after military-service toggles to yes
    triggerRef.current?.(VISA_GROUP_FIELDS.concat(VISA_UPLOAD_FIELD));
  }, [hasServed, presentNationality]);

  const isVisaOnArrival = visaType === VISA_ON_ARRIVAL_CODE;
  const isVisaExemptNationality = visaExemptNationalities.has(presentNationality?.trim() ?? "");
  const requiresVisaDetails = !isVisaExemptNationality && !isVisaOnArrival;

  useEffect(() => {
    const previousIsVisaOnArrival = previousVisaOnArrivalRef.current;
    previousVisaOnArrivalRef.current = isVisaOnArrival;

    if (previousIsVisaOnArrival === null || previousIsVisaOnArrival === isVisaOnArrival) {
      return;
    }

    if (!presentNationality?.trim()) {
      return;
    }

    triggerRef.current?.(VISA_GROUP_FIELDS.concat(VISA_UPLOAD_FIELD));
  }, [isVisaOnArrival, presentNationality]);

  return (
    <FormProvider {...formData}>
      <FormSection title={t("security-clearance.particulars-of-residence-visa")}>
        <FormItem<PortSecurityFormData, "visaType">
          required={!isVisaExemptNationality}
          name="visaType"
          type="autocomplete"
          options={formattedVisaTypes}
          label={t("security-clearance.visa-type")}
          testId="security-visaType"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "residenceVisaNo">
          name="residenceVisaNo"
          type="text"
          maxChars={200}
          label={t("security-clearance.residence-visa-no")}
          testId="security-residenceVisaNo"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "visaPlaceOfIssue">
          required={requiresVisaDetails}
          name="visaPlaceOfIssue"
          type="autocomplete"
          options={stateOptions}
          label={t("security-clearance.place-of-issue")}
          testId="security-visaPlaceOfIssue"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "visaExpiryDate">
          required={requiresVisaDetails}
          name="visaExpiryDate"
          type="date-picker"
          label={t("security-clearance.expiry-date")}
          testId="security-visaExpiryDate"
          onFieldChange={updateFormData}
          timePicker={false}
          dateConstraint="not-past"
        />
        <FormItem<PortSecurityFormData, "visaDateOfIssue">
          name="visaDateOfIssue"
          type="date-picker"
          label={t("security-clearance.date-of-issue")}
          testId="security-visaDateOfIssue"
          onFieldChange={updateFormData}
          timePicker={false}
          dateConstraint="not-future"
        />
      </FormSection>
    </FormProvider>
  );
};

export default ResidenceData;
